import { APPOINTMENT_NOTE_TYPES, CHECKLIST_TYPES, CHECK_EVENT_TYPE, DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS, STATUS_IDENTIFIERS } from "models";
import { MECHANIC_SORTING, WO_INTERNAL_FILTER } from "modules/Dayplanner";
import { updateAppointmentStatusIdentifier } from "utils/appointmentUtils";

import type { User } from "models";
import type { DayplannerAppointment, DayplannerColumns, MechanicWithAppointments } from "modules/Dayplanner";

type DayplannerStatusOption = { color: string; icon: string };

export const DayplannerStatusOptions = new Map<DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS, DayplannerStatusOption>([
  [DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.NewCarStatus, { color: "#F49523", icon: "car" }],
  [DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.CarInShop, { color: "#EFC56B", icon: "car garage" }],
  [DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.Panic, { color: "#DB2828", icon: "circle exclamation" }],
  [DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.AannameOKStatus, { color: "#ff5722", icon: "square a" }],
  [DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.HandleCheckIn, { color: "#5F7D8B", icon: "calendar check" }],
  [DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.CarCheckStatus, { color: "#F8C02B", icon: "wrench" }],
  [DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.DiagnoseStatus, { color: "#69BEC0", icon: "triangle exclamation" }],
  [DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.CustomerAnsweredStatus, { color: "#EA2D62", icon: "user check" }],
  [DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.CustomerOKStatus, { color: "#2D85D0", icon: "face smile regular" }],
  [DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.BackOrder, { color: "#6C0699", icon: "bo" }]
]);

export const matchesStatusFilter = (appointment: DayplannerAppointment, filterValue: DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS | null) => {
  switch (filterValue) {
    case DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.NewCarStatus:
      return [STATUS_IDENTIFIERS.NewCarStatus, STATUS_IDENTIFIERS.CheckInDone].includes(appointment.appointment_status_identifier);
    case DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.CarInShop:
      return !!(appointment.appointment_status_identifier === STATUS_IDENTIFIERS.CarInShop && appointment.car_in_shop && !appointment.car_out_of_shop);
    case DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.Panic:
      return appointment.has_panic;
    case DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.AannameOKStatus:
      return appointment.appointment_status_identifier === STATUS_IDENTIFIERS.AannameOKStatus;
    case DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.HandleCheckIn:
      return appointment.appointment_status_identifier === STATUS_IDENTIFIERS.HandleCheckIn;
    case DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.CarCheckStatus:
      return appointment.appointment_status_identifier === STATUS_IDENTIFIERS.CarCheckStatus;
    case DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.DiagnoseStatus:
      return appointment.appointment_status_identifier === STATUS_IDENTIFIERS.DiagnoseStatus;
    case DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.CustomerAnsweredStatus:
      return appointment.appointment_status_identifier === STATUS_IDENTIFIERS.CustomerAnsweredStatus;
    case DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.CustomerOKStatus:
      return appointment.appointment_status_identifier === STATUS_IDENTIFIERS.CustomerOKStatus;
    case DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.BackOrder:
      return appointment.appointment_status_identifier === STATUS_IDENTIFIERS.BackOrder;
    default:
      return false;
  }
};

export const ascendingSort = (a: MechanicWithAppointments, b: MechanicWithAppointments): number => {
  return (a.first_name + a.last_name).localeCompare(b.first_name + b.last_name);
};

export const descendingSort = (a: MechanicWithAppointments, b: MechanicWithAppointments): number => {
  return (b.first_name + b.last_name).localeCompare(a.first_name + a.last_name);
};

export const sortMechanics = (mechanics: MechanicWithAppointments[], mechanicsSorting: MECHANIC_SORTING, mechanicsPinned: number[]): MechanicWithAppointments[] => {
  const pinnedMechanics: MechanicWithAppointments[] = [];
  const mechanicWithAppointments: MechanicWithAppointments[] = [];
  const mechanicWithoutAppointments: MechanicWithAppointments[] = [];

  mechanics.forEach(mechanic => {
    if (mechanicsPinned.includes(mechanic.id)) pinnedMechanics.push(mechanic);
    else if (mechanic.appointments.length > 0) mechanicWithAppointments.push(mechanic);
    else mechanicWithoutAppointments.push(mechanic);
  });

  if (mechanicsSorting === MECHANIC_SORTING.Ascending) {
    pinnedMechanics.sort(ascendingSort);
    mechanicWithAppointments.sort(ascendingSort);
    mechanicWithoutAppointments.sort(ascendingSort);
  } else {
    pinnedMechanics.sort(descendingSort);
    mechanicWithAppointments.sort(descendingSort);
    mechanicWithoutAppointments.sort(descendingSort);
  }

  return [...pinnedMechanics, ...mechanicWithAppointments, ...mechanicWithoutAppointments];
};

const hasUnassignedAndNoDrive = (appointment: DayplannerAppointment) =>
  !appointment.assigned_mechanic &&
  appointment.car_in_shop &&
  !appointment.car_out_of_shop &&
  !!appointment.notes?.some(note => note.appointment_note_type_id === APPOINTMENT_NOTE_TYPES.NoDrive);

export const sortAppointments = (appointments: DayplannerAppointment[]) => {
  return appointments.sort((ac1: DayplannerAppointment, ac2: DayplannerAppointment) => {
    if (ac1.appointment_status_identifier === STATUS_IDENTIFIERS.BackOrder && ac2.appointment_status_identifier !== STATUS_IDENTIFIERS.BackOrder) return 1;
    if (ac2.appointment_status_identifier === STATUS_IDENTIFIERS.BackOrder && ac1.appointment_status_identifier !== STATUS_IDENTIFIERS.BackOrder) return -1;

    const ac1NoDrive = hasUnassignedAndNoDrive(ac1);
    const ac2NoDrive = hasUnassignedAndNoDrive(ac2);
    if (ac1NoDrive && !ac2NoDrive) return 1;
    if (ac2NoDrive && !ac1NoDrive) return -1;

    const ac1Time = ac1.car_return_time || ac1.planning_work_stop;
    const ac2Time = ac2.car_return_time || ac2.planning_work_stop;

    if (ac1Time && ac2Time) {
      return new Date(ac1Time).getTime() - new Date(ac2Time).getTime();
    }

    return Number(ac1.wo_nr) - Number(ac2.wo_nr);
  });
};

export const prepareDayplannerData = (mechanics: User[], appointments: DayplannerAppointment[], mechanicsSorting: MECHANIC_SORTING, mechanicsPinned: number[]) => {
  const columns: DayplannerColumns = {
    appointmentsColumn: [],
    unassignedColumn: [],
    mechanicsColumn: mechanics.map(mechanic => ({ ...mechanic, appointments: [] })),
    carReadyColumn: [],
    qualityControlColumn: []
  };

  const dayplannerAppointments: DayplannerAppointment[] = [];

  sortAppointments(appointments)
    .map(updateAppointmentStatusIdentifier)
    .forEach(appointment => {
      if (!appointment.assigned_mechanic) {
        switch (appointment.appointment_status_identifier) {
          case STATUS_IDENTIFIERS.CarReadyStatus:
          case STATUS_IDENTIFIERS.CarOkPlusRepairOverivew:
            columns.carReadyColumn.push(appointment);
            dayplannerAppointments.push(appointment);
            break;

          case STATUS_IDENTIFIERS.QualityCheckStatus:
          case STATUS_IDENTIFIERS.QualityCheckPlusRepairOverview:
            columns.qualityControlColumn.push(appointment);
            dayplannerAppointments.push(appointment);
            break;

          case STATUS_IDENTIFIERS.CanceledStatus:
            columns.appointmentsColumn.push(appointment);
            dayplannerAppointments.push(appointment);
            break;

          default:
            if (
              appointment.appointment_status_identifier === STATUS_IDENTIFIERS.CarCheckStartedStatus &&
              Number(appointment.car_check_events?.filter(ce => ce.checklist_type === CHECKLIST_TYPES.Quality && ce.type === CHECK_EVENT_TYPE.Started).length) >
                Number(
                  appointment.car_check_events?.filter(
                    ce => ce.checklist_type === CHECKLIST_TYPES.Quality && [CHECK_EVENT_TYPE.Aborted, CHECK_EVENT_TYPE.Completed].includes(ce.type)
                  ).length
                )
            ) {
              columns.qualityControlColumn.push(appointment);
              dayplannerAppointments.push(appointment);
            } else if (appointment.car_in_shop && !appointment.car_out_of_shop) {
              columns.unassignedColumn.push(appointment);
              dayplannerAppointments.push(appointment);
            } else if (appointment.car_out_of_shop) {
              columns.appointmentsColumn.push(appointment);
              dayplannerAppointments.push(appointment);
            } else {
              switch (appointment.appointment_status_identifier) {
                case STATUS_IDENTIFIERS.CarCheckStatus:
                case STATUS_IDENTIFIERS.BackOrder:
                case STATUS_IDENTIFIERS.PricingOKStatus:
                case STATUS_IDENTIFIERS.ContactStatus:
                case STATUS_IDENTIFIERS.CustomerOKStatus:
                case STATUS_IDENTIFIERS.DiagnoseStatus:
                  columns.unassignedColumn.push(appointment);
                  dayplannerAppointments.push(appointment);
                  break;

                default:
                  columns.appointmentsColumn.push(appointment);
                  dayplannerAppointments.push(appointment);
                  break;
              }
            }
        }
      } else {
        const assignedMechanic = columns.mechanicsColumn.find(mechanic => mechanic.id === appointment.assigned_mechanic);
        if (assignedMechanic) {
          const dayplannerAppointment = {
            ...appointment,
            workingOn: mechanics
              .filter(mechanic => mechanic.working_on === appointment.id && mechanic.id !== appointment.assigned_mechanic)
              .sort((m1, m2) => m2.updated_on.localeCompare(m1.updated_on))
          };
          assignedMechanic.appointments.push(dayplannerAppointment);
          dayplannerAppointments.push(appointment);
        }
      }
    });

  columns.mechanicsColumn = sortMechanics(columns.mechanicsColumn, mechanicsSorting, mechanicsPinned);

  return { columns, dayplannerAppointments };
};

export const appointmentMatchesSearch = (app: DayplannerAppointment, searchTerm: string) => {
  const lowerCaseSearchTerm = searchTerm.toLowerCase();
  return (
    app.wo_nr.toLowerCase().includes(lowerCaseSearchTerm) ||
    app.reg_number.toLowerCase().includes(lowerCaseSearchTerm) ||
    `${app.driver_firstname} ${app.driver_surname}`.toLowerCase().includes(lowerCaseSearchTerm) ||
    `${app.owner_firstname} ${app.owner_surname}`.toLowerCase().includes(lowerCaseSearchTerm)
  );
};

export const woInternalFiltered = (app: DayplannerAppointment, selectedWoInternalFilter: WO_INTERNAL_FILTER) => {
  return selectedWoInternalFilter === WO_INTERNAL_FILTER.Internal ? app.internal : !app.internal;
};

const filtersMatchingStatuses = [
  STATUS_IDENTIFIERS.NewCarStatus,
  STATUS_IDENTIFIERS.CheckInDone,
  STATUS_IDENTIFIERS.CarInShop,
  STATUS_IDENTIFIERS.AannameOKStatus,
  STATUS_IDENTIFIERS.HandleCheckIn,
  STATUS_IDENTIFIERS.CarCheckStatus,
  STATUS_IDENTIFIERS.DiagnoseStatus,
  STATUS_IDENTIFIERS.CustomerAnsweredStatus,
  STATUS_IDENTIFIERS.CustomerOKStatus,
  STATUS_IDENTIFIERS.BackOrder
];

export const applyFilters = (
  columns: DayplannerColumns,
  activeFilters: DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS[],
  searchTerm: string,
  selectedWoInternalFilter: WO_INTERNAL_FILTER,
  brandsFilter: string[]
): DayplannerColumns => {
  const matchFilterAndSearch = (appointment: DayplannerAppointment) => {
    const matchesFilter = activeFilters.some(filter => matchesStatusFilter(appointment, filter));
    const matchesBrandsFilter = brandsFilter.length > 0 ? brandsFilter.includes(appointment.car_make) : true;
    const matchesSearch = searchTerm ? appointmentMatchesSearch(appointment, searchTerm) : true;
    const matchesWoInternalFilter = selectedWoInternalFilter !== WO_INTERNAL_FILTER.All ? woInternalFiltered(appointment, selectedWoInternalFilter) : true;
    const isPanicFiltered = appointment.has_panic && !activeFilters.includes(DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS.Panic);
    const isMatchingStatus = filtersMatchingStatuses.includes(appointment.appointment_status_identifier) || appointment.has_panic;
    const isInactiveMatchingStatus = isPanicFiltered || !(matchesFilter && matchesSearch && matchesBrandsFilter && matchesWoInternalFilter && isMatchingStatus);
    const isInactiveNonMatchingStatus = !(matchesWoInternalFilter && matchesSearch && matchesBrandsFilter);

    return {
      ...appointment,
      inactive: isMatchingStatus ? isInactiveMatchingStatus : isInactiveNonMatchingStatus
    };
  };

  return {
    appointmentsColumn: columns.appointmentsColumn.map(matchFilterAndSearch),
    unassignedColumn: columns.unassignedColumn.map(matchFilterAndSearch),
    mechanicsColumn: columns.mechanicsColumn.map(mechanic => ({
      ...mechanic,
      appointments: mechanic.appointments.map(matchFilterAndSearch),
      auto_expand: searchTerm.length >= 3 && mechanic.appointments.some(appointment => appointmentMatchesSearch(appointment, searchTerm))
    })),
    carReadyColumn: columns.carReadyColumn.map(matchFilterAndSearch),
    qualityControlColumn: columns.qualityControlColumn.map(matchFilterAndSearch)
  };
};
